import { IconButton, useMediaQuery } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import ViewProposalIcon from "../../../../assets/images/view-proposal.svg";
import usePreviewProposal from "hooks/proposals/usePreviewProposal";
import { ProposalLayout } from "generated/graphql";
import useGlobalMessage from "hooks/notifications/useGlobalMessage";
import { getRoute } from "utils/routing";
import { useCallback, useState } from "react";
import ActionConfirmDialog from "components/ActionConfirmDialog/ActionConfirmDialog";
import useConfirmationDialog from "hooks/useConfirmationDialog";
import { getMissingJobSizeMessage } from "modules/proposals/validators";

const ViewProposalButton = ({ proposal, layout = ProposalLayout.SIMPLE }) => {
  const [previewProposal, { isSuccess: previewSuccess, loading }] = usePreviewProposal();
  const { show } = useGlobalMessage();
  const [error, setError] = useState<null | string>(null);

  const isMobile = useMediaQuery(
    "(max-width: 428px), screen and (min-width: 428px) and (max-width: 926px) and (orientation: landscape)"
  );

  const isDisabled = loading;

  const variables = {
    proposalId: proposal.id,
    layout: layout,
    removeUnitPriceAndQuantity: proposal.removeUnitPriceAndQuantity || false,
    removeIndividualLineItemPricing: proposal.removeIndividualLineItemPricing || false,
    attachments: proposal.attachments?.map((a) => a.id) || [],
  };

  const handleJobSizeError = (result) => {
    // @ts-ignore
    if (result?.message?.includes("Please input a job size.")) {
      // @ts-ignore
      setError(result.message);
      setOpen(true);
    } else {
      show({
        message: "Preview failed. Please try again.",
        options: { variant: "error" },
      });
    }
  };

  const handleViewProposal = async () => {
    const missingJobSizeMessage = getMissingJobSizeMessage(proposal);
    if (missingJobSizeMessage) {
      setError(missingJobSizeMessage);
      setOpen(true);
      return;
    }

    if (isMobile) {
      const previewUrl = getRoute("proposals.preview", [["proposalId", proposal.id]]);
      const urlParams = new URLSearchParams({
        ...variables,
        layout: variables.layout as string,
        removeUnitPriceAndQuantity: String(variables.removeUnitPriceAndQuantity ?? false),
        removeIndividualLineItemPricing: String(variables.removeIndividualLineItemPricing ?? false),
        attachments: JSON.stringify(variables.attachments || []),
      });
      window.open(`${previewUrl}?${urlParams.toString()}`, "_blank");
    } else {
      try {
        const result = await previewProposal({
          variables,
        });

        if (result.data?.previewProposal) {
          window.open(result.data.previewProposal, "_blank");
        } else {
          handleJobSizeError(result);
        }
      } catch (error) {
        handleJobSizeError(error);
      }
    }
  };

  const acceptFn = useCallback(async () => {
    if (isMobile) {
      const previewUrl = getRoute("proposals.preview", [["proposalId", proposal.id]]);
      const urlParams = new URLSearchParams({
        ...variables,
        layout: variables.layout as string,
        removeUnitPriceAndQuantity: String(variables.removeUnitPriceAndQuantity ?? false),
        removeIndividualLineItemPricing: String(variables.removeIndividualLineItemPricing ?? false),
        attachments: JSON.stringify(variables.attachments || []),
        forceSend: "true",
      });
      window.open(`${previewUrl}?${urlParams.toString()}`, "_blank");
    } else {
      const result = await previewProposal({
        variables: { ...variables, forceSend: true },
      });

      if (previewSuccess(result.data) && result.data?.previewProposal) {
        window.open(result.data.previewProposal, "_blank");
      }
    }
    return true;
  }, [variables, previewProposal, previewSuccess, isMobile, proposal.id]);

  const cancelFn = useCallback(async () => {
    return true;
  }, []);

  const { open, setOpen, onAccept, onCancel } = useConfirmationDialog({
    acceptFn,
    cancelFn,
  });

  return (
    <>
      <Tooltip title="View Proposal">
        <IconButton onClick={handleViewProposal} disabled={isDisabled}>
          <img src={ViewProposalIcon} alt="View Proposal" />
        </IconButton>
      </Tooltip>
      <ActionConfirmDialog
        open={open}
        setOpen={setOpen}
        handleAccept={onAccept}
        handleCancel={onCancel}
        title="Confirmation"
        content={
          error?.replace("Please input a job size.", "Proceed?") ||
          "Are you sure you want to proceed?"
        }
      />
    </>
  );
};

export default ViewProposalButton;
